import {
  AiMessageStatusEnum,
  AiStreamEventTypeEnum,
  AiToolStatusEnum,
} from 'graphql-common';

// One frame of the aiConversationStream subscription. Fields not applicable
// to the frame kind are null (see AiConversationStreamEvent).
export type AiStreamEvent = {
  eventType: AiStreamEventTypeEnum;
  messageId?: string | null;
  delta?: string | null;
  sequence?: number | null;
  status?: AiMessageStatusEnum | null;
  errorCode?: string | null;
  // TOOL frames only: a tool invocation, RUNNING then COMPLETED, paired by
  // toolCallId. They carry no `sequence` (tools run concurrently).
  toolCallId?: string | null;
  toolName?: string | null;
  toolStatus?: AiToolStatusEnum | null;
  suggestedFollowups?: string[] | null;
  conversationTitle?: string | null;
};

// A tool invocation observed on the live stream, keyed by toolCallId.
export type AiStreamToolCall = {
  toolName: string;
  status: AiToolStatusEnum;
};

type FrameRecord = {
  delta: string | null;
  status: AiMessageStatusEnum | null;
  errorCode: string | null;
};

export type AiStreamTurnState = {
  messageId: string | null;
  // Every MESSAGE frame (delta or status-only) consumes one sequence number,
  // starting at 1, monotonic per turn. Delivery order is not guaranteed.
  framesBySequence: Record<number, FrameRecord>;
  // Status/errorCode from the highest-sequence frame received so far —
  // a terminal frame may arrive before earlier deltas.
  status: AiMessageStatusEnum | null;
  errorCode: string | null;
  maxSequence: number;
  // Tool invocations seen this turn, keyed by toolCallId, in arrival order
  // (UUID keys preserve insertion order). RUNNING entries drive the live
  // "Searching documentation…" indicator.
  toolCalls: Record<string, AiStreamToolCall>;
  suggestedFollowups: string[];
  title: string | null;
  // Reasoning text accumulated from THINKING frames, in arrival order — the
  // model's extended-thinking output, streamed before the answer (guide §4).
  // Kept separate from the answer text and rendered in the "Thoughts" panel.
  thinking: string;
};

export const AI_STREAM_TURN_INITIAL_STATE: AiStreamTurnState = {
  messageId: null,
  framesBySequence: {},
  status: null,
  errorCode: null,
  maxSequence: 0,
  toolCalls: {},
  suggestedFollowups: [],
  title: null,
  thinking: '',
};

const TERMINAL_STATUSES = [
  AiMessageStatusEnum.Complete,
  AiMessageStatusEnum.Failed,
  AiMessageStatusEnum.Cancelled,
  AiMessageStatusEnum.TruncatedBudget,
];

export function isAiStreamTerminalStatus(
  status?: AiMessageStatusEnum | null,
): boolean {
  return !!status && TERMINAL_STATUSES.includes(status);
}

// Compile-time exhaustiveness guard: adding a new AiStreamEventTypeEnum member
// without a matching case above becomes a type error here. At runtime an
// unknown/future event type is ignored (the onData boundary logs dropped
// frames), so the reducer never throws on an unrecognized wire value.
function ignoreUnknownEvent(
  _eventType: never,
  state: AiStreamTurnState,
): AiStreamTurnState {
  return state;
}

export function applyAiStreamEvent(
  state: AiStreamTurnState,
  event: AiStreamEvent,
): AiStreamTurnState {
  switch (event.eventType) {
    case AiStreamEventTypeEnum.Title:
      return { ...state, title: event.conversationTitle ?? state.title };
    case AiStreamEventTypeEnum.Suggestions:
      // Keep existing suggestions if a frame ever arrives with a null payload,
      // rather than clobbering them with an empty list.
      return {
        ...state,
        suggestedFollowups:
          event.suggestedFollowups ?? state.suggestedFollowups,
      };
    // Reasoning deltas stream separately from — and before — the answer
    // (guide §4). Accumulate them in arrival order into their own buffer so the
    // "Thoughts" panel can render the reasoning without it bleeding into the
    // answer text. Providers that don't stream reasoning send a single THINKING
    // frame at completion instead of a run of deltas; appending handles both.
    case AiStreamEventTypeEnum.Thinking: {
      const { sequence, delta } = event;
      const hasSequence = typeof sequence === 'number' && sequence >= 1;
      // Redelivered thinking frame (same sequence): keep the first, don't
      // double-append.
      if (hasSequence && state.framesBySequence[sequence]) return state;
      if (!delta && !hasSequence) return state;
      const next: AiStreamTurnState = {
        ...state,
        thinking: delta ? state.thinking + delta : state.thinking,
      };
      // THINKING and MESSAGE frames share ONE monotonic per-turn sequence
      // counter (guide §4): reasoning streams first and consumes the low
      // sequence numbers, so in an extended-thinking turn the answer's MESSAGE
      // frames begin at sequence 3+, not 1. Record the reasoning sequence as a
      // filled-but-empty slot so getAiStreamText's contiguous-prefix walk treats
      // it as delivered and keeps assembling the answer — otherwise every answer
      // frame stalls forever behind the permanent "gap" the thinking sequences
      // leave at the front, leaving the turn stuck on the thinking indicator with
      // an empty answer until it settles from the persisted transcript.
      // Deliberately NOT routed through the MESSAGE branch: a THINKING frame
      // carries the reasoning row's messageId, not the answer's, and must never
      // become turn.messageId (used to match the persisted answer row).
      if (hasSequence) {
        next.framesBySequence = {
          ...state.framesBySequence,
          [sequence]: { delta: null, status: null, errorCode: null },
        };
      }
      return next;
    }
    case AiStreamEventTypeEnum.Tool: {
      const { toolCallId } = event;
      if (!toolCallId) return state;
      const existing = state.toolCalls[toolCallId];
      return {
        ...state,
        toolCalls: {
          ...state.toolCalls,
          [toolCallId]: {
            toolName: event.toolName ?? existing?.toolName ?? '',
            status:
              event.toolStatus ?? existing?.status ?? AiToolStatusEnum.Running,
          },
        },
      };
    }
    case AiStreamEventTypeEnum.Message: {
      const { sequence } = event;
      if (typeof sequence !== 'number' || sequence < 1) return state;
      // Duplicate frame (re-delivery): keep the first one.
      if (state.framesBySequence[sequence]) return state;
      const next: AiStreamTurnState = {
        ...state,
        messageId: state.messageId ?? event.messageId ?? null,
        framesBySequence: {
          ...state.framesBySequence,
          [sequence]: {
            delta: event.delta ?? null,
            status: event.status ?? null,
            errorCode: event.errorCode ?? null,
          },
        },
      };
      if (sequence > state.maxSequence) {
        next.maxSequence = sequence;
        next.status = event.status ?? state.status;
        next.errorCode = event.errorCode ?? state.errorCode;
      }
      return next;
    }
    default:
      return ignoreUnknownEvent(event.eventType, state);
  }
}

// Streamed text assembled from the contiguous prefix of received frames
// (sequence 1..N without gaps), so out-of-order delivery never shows
// mid-message gaps; frames ahead of a gap stay buffered until it fills.
export function getAiStreamText(state: AiStreamTurnState): string {
  let text = '';
  for (let sequence = 1; ; sequence += 1) {
    const frame = state.framesBySequence[sequence];
    if (!frame) break;
    if (frame.delta) text += frame.delta;
  }
  return text;
}

// True while the assistant has produced no visible output yet (queued or
// streaming with no contiguous text) — drives the "thinking" indicator.
export function isAiStreamThinking(state: AiStreamTurnState): boolean {
  if (isAiStreamTerminalStatus(state.status)) return false;
  return getAiStreamText(state) === '';
}

// The turn's accumulated reasoning text (extended-thinking deltas), in arrival
// order — rendered in the collapsible "Thoughts" panel, kept separate from the
// answer. Empty when the conversation is at STANDARD level or the model
// streamed no reasoning.
export function getAiStreamThinkingText(state: AiStreamTurnState): string {
  return state.thinking;
}

// Machine name of the tool currently running (most recent un-completed call),
// or null when no tool is active — drives the live tool-activity indicator.
export function getAiStreamRunningToolName(
  state: AiStreamTurnState,
): string | null {
  const running = Object.values(state.toolCalls).filter(
    (toolCall) => toolCall.status === AiToolStatusEnum.Running,
  );
  return running.length ? running[running.length - 1].toolName : null;
}

// A tool invocation of the live turn, ready to render as a step. `status` lets
// the renderer show a spinner while it runs and a check once it completes.
export type AiStreamToolStep = {
  id: string;
  toolName: string;
  status: AiToolStatusEnum;
};

// The turn's tool invocations in arrival order — both RUNNING and COMPLETED.
// Rendering the running one as an in-progress step (rather than a pill label
// that hands off to the strip only on completion) keeps a single tool row that
// transitions spinner → check in place, instead of the label teleporting from
// the thinking pill into the steps strip and the pill snapping back to the
// generic "thinking" copy. Steps stay on screen until the post-turn refetch
// lands the persisted rows. `id` is the BE's toolCallId, the same value the
// persisted AiToolCall carries, so live and persisted steps for one invocation
// dedupe against each other.
export function getAiStreamToolSteps(
  state: AiStreamTurnState,
): AiStreamToolStep[] {
  return Object.entries(state.toolCalls).map(([toolCallId, toolCall]) => ({
    id: toolCallId,
    toolName: toolCall.toolName,
    status: toolCall.status,
  }));
}
